import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsService, type Notification } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Bell, CheckCheck, Loader2, Plus, Search, Send, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "teacher", "student", "parent", "cashier"] as const;
type Role = (typeof ROLES)[number];

interface Template {
  key: string; label: string; title: string; message: string;
  target_role?: Role | ""; target_class?: string;
}
const TEMPLATES: Template[] = [
  {
    key: "ptm",
    label: "Parent-Teacher Meeting",
    title: "Parent-Teacher Meeting — Reminder",
    message: "This is to inform you that the PTM is scheduled on [Date] at [Time] in the school auditorium. Please make it a point to attend.",
    target_role: "parent",
  },
  {
    key: "holiday",
    label: "School holiday",
    title: "School Holiday Notification",
    message: "The school will remain closed on [Date] on account of [Reason]. Classes will resume on the next working day.",
    target_role: "",
  },
  {
    key: "exam",
    label: "Exam schedule",
    title: "Upcoming Examination — Schedule",
    message: "The [Term] examinations begin from [Start Date]. Please check the timetable circulated for your class and prepare accordingly.",
    target_role: "student",
  },
  {
    key: "fees",
    label: "Fee reminder",
    title: "Fee Payment Reminder",
    message: "This is a gentle reminder that the tuition fee for the current quarter is due. Please pay at the school cashier before the due date to avoid late fees.",
    target_role: "parent",
  },
  {
    key: "custom",
    label: "Blank",
    title: "",
    message: "",
    target_role: "",
  },
];

const ROLE_META: Record<string, { color: string; label: string }> = {
  admin: { color: "bg-indigo-500", label: "Admin" },
  teacher: { color: "bg-cyan-500", label: "Teacher" },
  student: { color: "bg-emerald-500", label: "Student" },
  parent: { color: "bg-amber-500", label: "Parent" },
  cashier: { color: "bg-fuchsia-500", label: "Cashier" },
};

export function NotificationsPage() {
  const { user } = useAuth();
  const canCompose = user?.role === "admin" || user?.role === "teacher";
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [composing, setComposing] = useState(false);
  const qc = useQueryClient();

  const { data: items = [], isPending } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsService.list(),
  });

  const filtered = useMemo(() => {
    return items.filter((n) => {
      if (roleFilter === "unread" && !n.unread) return false;
      if (roleFilter && roleFilter !== "unread" && n.target_role !== roleFilter) return false;
      if (!q) return true;
      const t = q.toLowerCase();
      return n.title.toLowerCase().includes(t) || n.message.toLowerCase().includes(t);
    });
  }, [items, q, roleFilter]);

  const unread = items.filter((n) => n.unread).length;

  const markAll = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: (r) => {
      toast.success(r.marked > 0 ? `Marked ${r.marked} as read` : "Already up to date");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-7 w-7 text-primary" /> Notifications
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {items.length} messages · <span className="text-primary font-semibold">{unread} unread</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {unread > 0 && (
            <Button variant="outline" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              {markAll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCheck className="mr-2 h-4 w-4" />}
              Mark all read
            </Button>
          )}
          {canCompose && (
            <Button onClick={() => setComposing(true)}>
              <Plus className="mr-2 h-4 w-4" /> Compose
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or message…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1">
          <FilterChip active={roleFilter === ""} onClick={() => setRoleFilter("")}>All</FilterChip>
          <FilterChip active={roleFilter === "unread"} onClick={() => setRoleFilter("unread")}>
            Unread {unread > 0 && <span className="ml-1 rounded-full bg-primary/20 px-1.5 text-[10px] font-bold">{unread}</span>}
          </FilterChip>
          {ROLES.map((r) => (
            <FilterChip key={r} active={roleFilter === r} onClick={() => setRoleFilter(r)}>
              <span className={`mr-1 inline-block h-2 w-2 rounded-full ${ROLE_META[r].color}`} />
              {ROLE_META[r].label}
            </FilterChip>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border bg-slate-50 p-10 text-center text-muted-foreground dark:bg-slate-950/30">
          <Bell className="h-6 w-6" />
          <div className="font-medium">No notifications match your filter</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((n) => (
            <NotificationItem key={n.id} n={n} />
          ))}
        </div>
      )}

      {composing && <ComposeDialog onClose={() => setComposing(false)} />}
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition
        ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
    >
      {children}
    </button>
  );
}

function NotificationItem({ n }: { n: Notification }) {
  const qc = useQueryClient();
  const markRead = useMutation({
    mutationFn: () => notificationsService.markRead(n.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  return (
    <Card className={n.unread ? "border-primary/50 shadow-sm" : ""}
      onClick={() => n.unread && markRead.mutate()}
      role="button"
      tabIndex={0}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base flex items-center gap-2">
            {n.unread && <span className="h-2 w-2 rounded-full bg-primary" aria-label="unread" />}
            {n.title}
          </CardTitle>
          <div className="mt-1 text-xs text-muted-foreground">
            From {n.created_by_name} · {new Date(n.created_at).toLocaleString("en-IN", {
              day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {!n.target_role && !n.target_class && <Badge variant="outline">Broadcast</Badge>}
          {n.target_role && (
            <Badge variant="outline" className="capitalize">
              <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_META[n.target_role]?.color || "bg-slate-400"}`} />
              {n.target_role}
            </Badge>
          )}
          {n.target_class && <Badge variant="secondary">Class {n.target_class}</Badge>}
        </div>
      </CardHeader>
      <CardContent><p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{n.message}</p></CardContent>
    </Card>
  );
}

function ComposeDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [templateKey, setTemplateKey] = useState<string>("custom");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetRole, setTargetRole] = useState<string>("");
  const [targetClass, setTargetClass] = useState<string>("");

  const applyTemplate = (key: string) => {
    setTemplateKey(key);
    const t = TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setTitle(t.title);
    setMessage(t.message);
    setTargetRole(t.target_role || "");
  };

  const classesQ = useQuery({
    queryKey: ["notifications", "classes"],
    queryFn: () => notificationsService.classes(),
  });

  const previewQ = useQuery({
    queryKey: ["notifications", "preview", targetRole, targetClass],
    queryFn: () => notificationsService.recipientsPreview(targetRole || null, targetClass || null),
  });

  const send = useMutation({
    mutationFn: () => notificationsService.create({
      title: title.trim(),
      message: message.trim(),
      target_role: targetRole || undefined,
      target_class: targetClass || undefined,
    }),
    onSuccess: () => {
      toast.success("Notification sent");
      qc.invalidateQueries({ queryKey: ["notifications"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Send failed"),
  });

  const canSend = title.trim().length > 0 && message.trim().length > 0;
  const recipients = previewQ.data?.count;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" /> Compose announcement
          </DialogTitle>
          <DialogDescription>
            Pick a template to prefill, choose an audience, and preview how many people will receive it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Templates */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Templates
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.key} type="button"
                  onClick={() => applyTemplate(t.key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition
                    ${templateKey === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. PTM on Saturday" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Message <span className="text-muted-foreground/70">({message.length}/1000)</span>
            </Label>
            <Textarea
              value={message} rows={5}
              onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
              placeholder="Detail of the announcement…"
              className="whitespace-pre-line"
            />
          </div>

          {/* Audience */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Target role</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                <button type="button" onClick={() => setTargetRole("")}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition
                    ${targetRole === "" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                  All roles
                </button>
                {ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => setTargetRole(r)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition
                      ${targetRole === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                    <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_META[r].color}`} />
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Target class (optional)</Label>
              <select value={targetClass} onChange={(e) => setTargetClass(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">— No class filter —</option>
                {(classesQ.data || []).map((c) => (
                  <option key={c} value={c}>Class {c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview card */}
          <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-semibold">Estimated recipients</span>
            </div>
            <div className="text-primary font-bold text-lg">
              {previewQ.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : (recipients ?? "?")}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {targetRole === "" && targetClass === "" && "Broadcast to all active users."}
            {targetRole && targetClass && `Users whose role is “${targetRole}” or parents linked to Class ${targetClass}.`}
            {targetRole && !targetClass && `All active ${targetRole}s across all classes.`}
            {!targetRole && targetClass && `Anyone tied to Class ${targetClass} (students + their parents).`}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => send.mutate()} disabled={!canSend || send.isPending}>
            {send.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send to {recipients ?? "…"} recipient{recipients === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
